import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { BigNumber } from "bignumber.js"; // Assuming you use bignumber.js
import config from "../config";
import { Farm } from "../interfaces/network";
import useFarmCommon from "../hooks/useFarmCommon";
import { NewPair, Pair } from "../interfaces/token";
import { useTokenHelpers } from "../hooks/useTokenHelpers";
import { useWriteContract, useChainId, usePublicClient } from "wagmi";
import useLazyReadContractAsync from "../hooks/useLazyReadContractAsync";
import ERC20 from "../../src/assets/abi/farm/ERC20.json";
import CommonFarms from "../../src/assets/abi/farm/CommonFarms.json";
import useDirectExchangeHelpers from "../hooks/useDirectExchangeHelpers";

// Module-level cache for external supply API with 60 second TTL
const externalSupplyCache: Record<string, { data: any; timestamp: number }> =
  {};
const SUPPLY_CACHE_TTL = 60000; // 60 seconds cache

interface Props {
  children: React.ReactNode;
}

export interface PairExtras {
  [key: string]: {
    totalStaked: BigNumber;
    apr: BigNumber;
    lpPerUsd: BigNumber;
    lastRewardBlock: number;
  };
}

interface FarmContextType {
  farmEnv: Farm[] | undefined;
  isReadyForGetFarmInfo: boolean;
  depositLpToken: (
    contractAddress: string,
    poolId: number,
    account: string,
    inputAmount: BigNumber,
    events: any
  ) => Promise<any>;
  withdrawLpToken: (
    contractAddress: string,
    poolId: number,
    account: string,
    inputAmount: BigNumber,
    events: any
  ) => Promise<any>;
  claimLpToken: (
    contractAddress: string,
    poolId: number,
    account: string,
    events: any
  ) => Promise<any>;
  getFarmInfo: (
    processEnvFarm: Farm,
    isEvry?: boolean
  ) => Promise<{
    pairExtras: PairExtras;
    rewardPerUsd: BigNumber;
    rewardPerBlock: BigNumber;
  }>;
  getTotalSupplyExternalApi: (token: string) => Promise<any>;
  getBalanceOf: (code: string, address: string) => Promise<any>;
  getPendingReward: (
    contractAddress: string,
    pid: number,
    account: string
  ) => Promise<any>;
  isApprovedAllowance: (
    account: string,
    spender: string,
    lpName: string
  ) => Promise<boolean>;
  getUserInfo: (
    contractAddress: string,
    pid: number,
    account: string
  ) => Promise<
    | {
        amount: BigNumber;
      }
    | {
        amount: null;
      }
  >;
  depositLpTokenWithCheckAllowance: (
    lpName: string,
    lpAddress: string,
    contractAddress: string,
    poolId: number,
    account: string,
    inputAmount: string,
    events: any
  ) => Promise<void>;
  // Add other methods here as needed
}

// Initialize the context
const FarmContext = createContext<FarmContextType | undefined>(undefined);

export const useFarm = () => {
  const context = useContext(FarmContext);
  if (!context) {
    throw new Error("useFarm must be used within a FarmProvider");
  }
  return context;
};

// The Provider component
export const FarmProvider: React.FC<Props> = ({ children }) => {
  const [farmEnv, setFarmEnv] = useState<Farm[]>();
  const [isReadyForGetFarmInfo, setIsReadyForGetFarmInfo] = useState(false);
  const { getTokenRationOmniPrice } = useDirectExchangeHelpers();

  const chainId = useChainId();
  const { getMapIDofReserves, getPrice, approveToken } = useFarmCommon();
  const { getAllowance } = useTokenHelpers();
  const { writeContractAsync } = useWriteContract();
  const { lazyReadContract } = useLazyReadContractAsync();
  const client = usePublicClient();

  const initFarmEnv = (chainId: number = config.chainId) => {
    const currentFarmEnv = config.networks?.[chainId]?.farm;

    setFarmEnv(
      currentFarmEnv && Object.keys(currentFarmEnv).length > 0
        ? currentFarmEnv
        : config.networks?.[config.chainId].farm
    );
  };

  useEffect(() => {
    initFarmEnv(chainId || config.chainId);
  }, [chainId]);

  useEffect(() => {
    setIsReadyForGetFarmInfo(true);
  }, []);

  const depositLpToken = async (
    contractAddress: string,
    poolId: number,
    account: string,
    inputAmount: BigNumber,
    events: any
  ) => {
    const result = await writeContractAsync(
      // @ts-ignore
      {
        // abi: AutoRouter,
        // account: account.current,
        abi: CommonFarms,
        // @ts-ignore
        from: account,
        // @ts-ignore
        address: contractAddress,
        functionName: "deposit",
        args: [account, poolId, inputAmount.toFixed(0)],
      },
      {
        onSuccess(data: any, variables: any, context: any) {
          // events.onReceipt({ transactionHash: data, status: true });
          return data;
        },
        onError: (error: any) => {
          events.onError(error);
        },
      }
    );
    // if (ethereum?.contracts != null) {
    //   try {
    //     const receipt = await farmContract[contractAddress].methods
    //       .deposit(account, poolId, inputAmount.toString())
    //       .send({ from: account })
    //       .on("transactionHash", (txHash: string) => {
    //         events.onTransactionHash(txHash);
    //       })
    //       .on("receipt", (receipt: any) => {
    //         events.onReceipt(receipt);
    //       })
    //       .on("error", (err: any, receipt: any) => {
    //         events.onError(err, receipt);
    //         throw err;
    //       });

    //     console.log("depositLpToken receipt", receipt);

    //     return receipt;
    //   } catch (error) {
    //     console.error("Error in depositLpToken:", error);
    //     return null;
    //   }
    // }
    if (result && client) {
      try {
        const receipt = await client.waitForTransactionReceipt({
          hash: result,
        });
        events.onReceipt({
          transactionHash: result,
          status: receipt.status === "success",
        });
      } catch (e) {
        console.error(e);
        events.onError(e);
      }
    }

    return { transactionHash: result, status: Boolean(result) };
  };

  const withdrawLpToken = async (
    contractAddress: string,
    poolId: number,
    account: string,
    inputAmount: BigNumber,
    events: any
  ) => {
    const result = await writeContractAsync(
      // @ts-ignore
      {
        // abi: AutoRouter,
        // account: account.current,
        abi: CommonFarms,
        // @ts-ignore
        from: account,
        // @ts-ignore
        address: contractAddress,
        functionName: "withdraw",
        args: [account, poolId, inputAmount.toFixed(0)],
      },
      {
        onSuccess(data: any, variables: any, context: any) {
          // events.onReceipt({ transactionHash: data, status: true });
          return data;
        },
        onError: (error: any) => {
          events.onError(error);
        },
      }
    );
    // if (ethereum?.contracts != null) {
    //   try {
    //     const receipt = await farmContract[contractAddress].methods
    //       .withdraw(account, poolId, inputAmount.toString())
    //       .send({ from: account })
    //       .on("transactionHash", (txHash: string) => {
    //         events.onTransactionHash(txHash);
    //       })
    //       .on("receipt", (receipt: any) => {
    //         events.onReceipt(receipt);
    //       })
    //       .on("error", (err: any, receipt: any) => {
    //         events.onError(err, receipt);
    //         throw err;
    //       });

    //     return receipt;
    //   } catch (error) {
    //     console.error("Error in withdrawLpToken:", error);
    //     return null;
    //   }
    // }
    if (result && client) {
      try {
        const receipt = await client.waitForTransactionReceipt({
          hash: result,
        });
        events.onReceipt({
          transactionHash: result,
          status: receipt.status === "success",
        });
      } catch (e) {
        console.error(e);
        events.onError(e);
      }
    }

    return { transactionHash: result, status: Boolean(result) };
  };

  const claimLpToken = async (
    contractAddress: string,
    poolId: number,
    account: string,
    events: any
  ) => {
    const result = await writeContractAsync(
      // @ts-ignore
      {
        abi: CommonFarms,
        // @ts-ignore
        from: account,
        // @ts-ignore
        address: contractAddress,
        functionName: "harvest",
        args: [poolId],
      },
      {
        onSuccess(data: any, variables: any, context: any) {
          if (events?.onTransactionHash) {
            events.onTransactionHash(data);
          }
          return data;
        },
        onError: (error: any) => {
          if (events?.onError) {
            events.onError(error);
          }
        },
      }
    );

    if (result && client) {
      try {
        const receipt = await client.waitForTransactionReceipt({
          hash: result,
        });
        if (events?.onReceipt) {
          events.onReceipt({
            transactionHash: result,
            status: receipt.status === "success",
          });
        }
      } catch (e) {
        console.error(e);
        if (events?.onError) {
          events.onError(e);
        }
      }
    }

    return { transactionHash: result, status: Boolean(result) };
  };

  const getEvryEmissionPerBlock = async (contractAddress: string) => {
    try {
      const result = await lazyReadContract(
        CommonFarms,
        contractAddress,
        "evryPerBlock",
        []
      );
      return result ? BigNumber(result.toString()) : null;
    } catch (error) {
      console.error("Error in getEvryEmissionPerBlock:", error);
      return null;
    }
  };

  const getRewardTokenPerBlock = async (contractAddress: string) => {
    try {
      const result = await lazyReadContract(
        CommonFarms,
        contractAddress,
        "rewardPerBlock",
        []
      );
      return result ? BigNumber(result.toString()) : null;
    } catch (error) {
      console.error("Error in getRewardTokenPerBlock:", error);
      return null;
    }
  };

  const getTotalSupply = async (lpAddress: string) => {
    try {
      const supply = await lazyReadContract(
        ERC20,
        lpAddress,
        "totalSupply",
        []
      );
      return supply ? BigNumber(supply.toString()) : BigNumber(0);
    } catch (e) {
      console.error("Error in getTotalSupply:", e);
      return BigNumber(0);
    }
  };

  const getListTotalSupply = async (lpAddresses: string[]) => {
    try {
      const listSupply = await Promise.all(
        lpAddresses.map((lpAddress) => getTotalSupply(lpAddress))
      );
      return listSupply;
    } catch (e) {
      return null;
    }
  };

  const getRewardTokenPerYear = (rewardPerBlock: BigNumber) => {
    return rewardPerBlock.multipliedBy(config.blockPerDay * 365);
  };

  const getAllPairInfo = async (processEnvFarm: Farm, isEvry = false) => {
    const { pairs, rewardToken, contractAddress } = processEnvFarm;

    try {
      const totalStaked = await Promise.all(
        pairs?.map(async (pair) => {
          try {
            if (pair.id === rewardToken.code) {
              const result = await lazyReadContract(
                CommonFarms,
                contractAddress ?? "",
                isEvry ? "evrySupply" : "rewardTokenSupply",
                []
              );
              return result ? result.toString() : "0";
            }

            const balance = await lazyReadContract(
              ERC20,
              pair.staking.address || "",
              "balanceOf",
              [contractAddress]
            );
            return balance ? balance.toString() : "0";
          } catch (e) {
            return "0";
          }
        })
      );

      const info = await Promise.all(
        pairs.map(async (pair, index) => {
          try {
            const poolInfo = await lazyReadContract(
              CommonFarms,
              contractAddress ?? "",
              "poolInfo",
              [pair.poolID]
            );
            return poolInfo;
          } catch (e) {
            console.error(`❌ Error fetching poolInfo for ${pair.id}:`, e);
            return null;
          }
        })
      );

      return pairs?.map((pair, idx) => {
        let totalStakedValue = BigNumber(totalStaked[idx]);
        // TODO: refactor later
        if (config.token[pair.id]?.decimals) {
          const decimals = 18 - (config.token[pair.id].decimals ?? 0);
          totalStakedValue = totalStakedValue.multipliedBy(`1e${decimals}`);
        }

        const pairResult = {
          ...pair,
          totalStaked: totalStakedValue,
          info: info[idx],
        };

        // Extract allocation point from poolInfo array (index 1)
        const allocPoint =
          info[idx] && Array.isArray(info[idx]) ? (info[idx] as any)[1] : null;

        return {
          ...pairResult,
          allocPoint: allocPoint
            ? BigNumber(allocPoint.toString())
            : BigNumber(0),
        };
      });
    } catch (error) {
      console.error("Error in getAllPairInfo:", error);
      return null;
    }
  };

  const getFarmInfo = async (processEnvFarm: Farm, isEvry = false) => {
    const rewardPerBlock = isEvry
      ? await getEvryEmissionPerBlock(processEnvFarm.contractAddress ?? "")
      : await getRewardTokenPerBlock(processEnvFarm.contractAddress ?? "");

    if (!rewardPerBlock || rewardPerBlock.isZero()) {
      console.warn(
        `⚠️ rewardPerBlock is zero or null for ${processEnvFarm.rewardToken.code}`
      );
    }

    const result: {
      pairExtras: PairExtras;
      rewardPerUsd: BigNumber;
      rewardPerBlock: BigNumber;
    } = {
      pairExtras: {},
      rewardPerUsd: BigNumber("0"),
      rewardPerBlock: rewardPerBlock ?? BigNumber("0"),
    };

    // ------------ Total supply for each token (including the one not in farm) --------------- //
    const farmAddresses = processEnvFarm.pairs.map(
      (pool) => pool.staking.address || ""
    );
    const farmIds = processEnvFarm.pairs.map((pool) => pool.id);
    const mapTotalSupply = (await getListTotalSupply(farmAddresses))?.reduce(
      (map: { [key: string]: BigNumber }, totalSupply, i) => {
        map[farmIds[i]] = totalSupply ?? BigNumber("0");
        return map;
      },
      {} as { [key: string]: BigNumber }
    );

    // --------------------- reserve ratio each pair (AMM + DMM) ------------------------ //
    const pairIds = (processEnvFarm.pairs as (Pair | NewPair)[])
      .filter((o: Pair | NewPair) => !!o.lpType)
      .map((pool: Pair | NewPair) => pool.id);

    const mapIDofReserves = await getMapIDofReserves(pairIds);

    const tokens = processEnvFarm.tokens;
    const tokenPrice = await getPrice(tokens, mapIDofReserves);

    if (processEnvFarm.rewardToken.code === "OMNI_TOKEN") {
      const tokenRatio: any = await getTokenRationOmniPrice();
      result.rewardPerUsd = tokenRatio;
    } else {
      const priceFromMap = tokenPrice[processEnvFarm.rewardToken.symbol];
      result.rewardPerUsd = BigNumber(priceFromMap);
    }

    const rewardTokenPerYear = getRewardTokenPerYear(result.rewardPerBlock);
    const rewardTokenPerYearUSD = rewardTokenPerYear.multipliedBy(
      result.rewardPerUsd
    );

    if (rewardTokenPerYearUSD.isNaN() || !rewardTokenPerYearUSD.isFinite()) {
      console.error(
        `❌ Invalid rewardTokenPerYearUSD: ${rewardTokenPerYearUSD}`
      );
      console.error(`❌ rewardTokenPerYear: ${rewardTokenPerYear}`);
      console.error(`❌ result.rewardPerUsd: ${result.rewardPerUsd}`);
    }

    // -------------- get total farm allocation --------------- //
    const pairs: any = await getAllPairInfo(processEnvFarm, isEvry);

    if (!pairs || pairs.length === 0) {
      console.error(`❌ No pairs data returned from getAllPairInfo`);
      return result;
    }

    const totalAllocPoint = pairs?.reduce((value: any, pair: any) => {
      const allocPoint = pair?.allocPoint
        ? BigNumber(pair.allocPoint.toString())
        : BigNumber(0);
      return allocPoint.plus(value);
    }, BigNumber("0"));

    // ----------------  calculate APR --------------- //
    for (const key in pairs) {
      const pair = pairs[key];
      let reserveTotalUsd = BigNumber("0");
      const reservePair = mapIDofReserves[pair.id];
      let lpPerUsd;
      if (!pair.lpType) {
        lpPerUsd = BigNumber(tokenPrice[pair.staking.symbol]);
      } else {
        const reserveTotalUsd0 = reservePair?.reserves?.[
          reservePair.token0?.code
        ]
          ? reservePair.reserves[reservePair.token0.code].multipliedBy(
              tokenPrice[reservePair.token0.symbol] || 0
            )
          : BigNumber("0");
        const reserveTotalUsd1 = reservePair?.reserves?.[
          reservePair.token1?.code
        ]
          ? reservePair.reserves[reservePair.token1.code].multipliedBy(
              tokenPrice[reservePair.token1.symbol] || 0
            )
          : BigNumber("0");

        reserveTotalUsd = reserveTotalUsd0.plus(reserveTotalUsd1);

        lpPerUsd =
          pair.totalStaked.isGreaterThan(BigNumber(0)) &&
          mapTotalSupply &&
          mapTotalSupply[pair.id] &&
          mapTotalSupply[pair.id].isGreaterThan(0)
            ? reserveTotalUsd.div(mapTotalSupply[pair.id])
            : BigNumber(0);
      }

      const pairAllocPoint = pair?.allocPoint
        ? BigNumber(pair.allocPoint.toString())
        : BigNumber(0);

      const totalStaked = pair.totalStaked;

      // Protect against division by zero and invalid values
      let apr = BigNumber("0");
      if (
        rewardTokenPerYearUSD.isGreaterThan(0) &&
        pairAllocPoint.isGreaterThan(0) &&
        totalAllocPoint.isGreaterThan(0) &&
        lpPerUsd.isGreaterThan(0) &&
        totalStaked.isGreaterThan(0)
      ) {
        apr = rewardTokenPerYearUSD
          .multipliedBy(pairAllocPoint.div(totalAllocPoint))
          .div(lpPerUsd.multipliedBy(totalStaked));
      }

      const lastRewardBlock = pair?.info?.lastRewardBlock || 0;
      result.pairExtras[pair.id] = {
        totalStaked,
        apr: apr.isNaN() ? BigNumber("0") : apr,
        lpPerUsd,
        lastRewardBlock,
      };
    }

    return result;
  };

  const getTotalSupplyExternalApi = async (token: string) => {
    if (!token) return 0;

    const cacheKey = `supply_${token}`;
    const now = Date.now();
    const cached = externalSupplyCache[cacheKey];

    // Return cached data if still valid
    if (cached && now - cached.timestamp < SUPPLY_CACHE_TTL) {
      return cached.data?.market_data?.total_supply || 0;
    }

    try {
      const url = config.externalPriceAPI + token;
      const response = await fetch(url);

      if (!response.ok) {
        // On rate limit or error, return cached data if available (even if expired)
        if (cached) {
          console.warn(
            `getTotalSupplyExternalApi: Rate limited, using stale cache for ${token}`
          );
          return cached.data?.market_data?.total_supply || 0;
        }
        console.error(
          `getTotalSupplyExternalApi: HTTP ${response.status} for ${token}`
        );
        return 0;
      }

      const supplySet = await response.json();
      externalSupplyCache[cacheKey] = { data: supplySet, timestamp: now };

      return supplySet?.market_data?.total_supply || 0;
    } catch (e) {
      // On error, return cached data if available
      if (cached) {
        console.warn(
          `getTotalSupplyExternalApi: Error fetching, using stale cache for ${token}`
        );
        return cached.data?.market_data?.total_supply || 0;
      }
      console.log("getTotalSupplyExternalApi", e);
      return 0;
    }
  };

  const getBalanceOf = async (code: string, address: string) => {
    try {
      const balance = await lazyReadContract(ERC20, code, "balanceOf", [
        address,
      ]);
      return balance ? balance.toString() : "0";
    } catch (error) {
      console.error("Error in getBalanceOf:", error);
      return "0";
    }
  };

  const getPendingReward = async (
    contractAddress: string,
    pid: number,
    account: string
  ) => {
    try {
      const result = await lazyReadContract(
        CommonFarms,
        contractAddress,
        "pendingReward",
        [pid, account]
      );
      return result ? BigNumber(result.toString()) : null;
    } catch (e) {
      console.log("Err: getPendingReward", e);
      return null;
    }
  };

  const isApprovedAllowance = useCallback(
    async (account: string, spender: string, lpName: string) => {
      if (lpName === "BNB") return true;

      const allowance: any = await lazyReadContract(
        ERC20,
        lpName,
        "allowance",
        [account, spender]
      );
      if (allowance === null) return false;

      // const allow = await tokenContract[lpName].methods
      //   .allowance(account, spender)
      //   .call();
      return !BigNumber(allowance).isEqualTo(0);
    },
    [lazyReadContract]
  );

  const getUserInfo = async (
    contractAddress: string,
    pid: number,
    account: string
  ) => {
    try {
      const userInfo: any = await lazyReadContract(
        CommonFarms,
        contractAddress,
        "userInfo",
        [pid, account]
      );

      if (userInfo) {
        // Handle array format: [amount, rewardDebt, ...]
        let amount = "0";
        if (Array.isArray(userInfo)) {
          amount = userInfo[0]?.toString() || "0";
        } else if (userInfo.amount) {
          amount = userInfo.amount.toString();
        }

        const result = {
          amount: BigNumber(amount),
        };
        return result;
      }

      return {
        amount: BigNumber("0"),
      };
    } catch (e) {
      console.error("❌ Error in getUserInfo:", {
        contractAddress,
        pid,
        account,
        error: e,
      });
      return {
        amount: BigNumber("0"),
      };
    }
  };

  const depositLpTokenWithCheckAllowance = async (
    lpName: string,
    lpAddress: string,
    contractAddress: string,
    poolId: number,
    account: string,
    inputAmount: string,
    events: any
  ) => {
    const spender = contractAddress;
    const amount = BigNumber(inputAmount);

    try {
      const allowance = await getAllowance(lpName, account, spender, lpAddress);
      if (allowance && BigNumber(allowance).isLessThan(amount)) {
        await approveToken(contractAddress, lpAddress, account, {
          onTransactionHash: (txHash: any) => {},
          onReceipt: ({
            transactionHash,
            status,
          }: {
            transactionHash: any;
            status: any;
          }) => {},
          onError: (_: any, receipt: any) => {},
        });
      }

      await depositLpToken(contractAddress, poolId, account, amount, events);
    } catch (error) {
      console.error("Error in depositLpTokenWithCheckAllowance:", error);
      events.onError(error);
    }
  };

  return (
    <FarmContext.Provider
      value={{
        farmEnv,
        isReadyForGetFarmInfo: isReadyForGetFarmInfo,
        depositLpToken,
        withdrawLpToken,
        claimLpToken,
        getFarmInfo,
        getTotalSupplyExternalApi,
        getBalanceOf,
        getPendingReward,
        isApprovedAllowance,
        getUserInfo,
        depositLpTokenWithCheckAllowance,
        // Add other methods to context as necessary
      }}
    >
      {children}
    </FarmContext.Provider>
  );
};
